import classNames from 'classnames';
import React from 'react';
import SVGIcon from '../../components/SVGIcon';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import arrowLeftIcon from '../../icons/non-prefilled/16x16/arrowLeft.svg';
import arrowRightIcon from '../../icons/non-prefilled/16x16/arrowRight.svg';
import retryIcon from '../../icons/non-prefilled/16x16/retry.svg';
import Link from '../../utils/router/Link';
import postPath, { publicPostPath } from '../../utils/url/postPath';
import useIsPublicView from '../../utils/useIsPublicView';
import * as styles from './PostSeriesFooter.module.scss';

const POST_FRAGMENT = gql(`
  fragment PaginationItem_post on PostFields {
    id
    ...postPath_post
    ...publicPostPath_post
  }
`);

const PAGINATION_ITEM_TYPES = {
  prev: {
    label: 'Prev',
    icon: arrowLeftIcon,
  },
  next: {
    label: 'Next',
    icon: arrowRightIcon,
  },
  restart: {
    label: 'Back to beginning',
    icon: retryIcon,
  },
} as const;

interface PaginationItemProps {
  post?: DocumentType<typeof POST_FRAGMENT> | null;
  type: keyof typeof PAGINATION_ITEM_TYPES;
  onBeforeNavigate?: (
    event: React.MouseEvent<HTMLElement>,
    postId: string,
  ) => void;
}

const PaginationItem = ({
  post,
  type,
  onBeforeNavigate,
}: PaginationItemProps) => {
  const isPublicView = useIsPublicView();

  return (
    post && (
      <Link
        to={isPublicView ? publicPostPath(post) : postPath(post)}
        className={classNames(styles.paginationItem, {
          [styles.prev]: type === 'prev',
          [styles.nextOrRestart]: type !== 'prev',
        })}
        onClick={(event) => onBeforeNavigate?.(event, post.id)}
      >
        <div className={styles.postDetails}>
          <p className={styles.meta}>{PAGINATION_ITEM_TYPES[type].label}</p>
          <p className={styles.postTitle}>{post.title}</p>
        </div>
        <SVGIcon className={styles.icon}>
          {PAGINATION_ITEM_TYPES[type].icon}
        </SVGIcon>
      </Link>
    )
  );
};

const SERIES_FRAGMENT = gql(`
  fragment PostSeriesFooter_series on SeriesFields {
    id
    posts {
      ...PaginationItem_post
    }
  }
`);

interface PostSeriesFooterProps {
  postId: string;
  series: DocumentType<typeof SERIES_FRAGMENT>;
  onBeforeNavigate?: (
    event: React.MouseEvent<HTMLElement>,
    postId: string,
  ) => void;
}

const PostSeriesFooter = ({
  postId,
  series,
  onBeforeNavigate,
}: PostSeriesFooterProps) => {
  const index = series.posts.findIndex((post) => post.id === postId);
  if (index === -1 || series.posts.length <= 1) {
    return null;
  }

  const prev = series.posts[index - 1];
  const nextOrFirst = series.posts[index + 1] ?? series.posts[0];
  const isLastInSeries = index >= series.posts.length - 1;

  return (
    <div className={styles.container}>
      <PaginationItem
        post={prev}
        type="prev"
        onBeforeNavigate={onBeforeNavigate}
      />
      {nextOrFirst !== prev && (
        <PaginationItem
          post={nextOrFirst}
          type={isLastInSeries ? 'restart' : 'next'}
          onBeforeNavigate={onBeforeNavigate}
        />
      )}
    </div>
  );
};

export default PostSeriesFooter;
